import { useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { emailCode, ensureSession, phoneCode } from '../lib/auth.ts';
import { checkEmail, checkPhone, COUNTRIES, flag, formatNational, guessCountry, nationalDigits } from '../lib/contact.ts';
import { cleanHandleInput, useHandleCheck } from '../lib/handle-check.ts';
import { refreshMe } from '../lib/me.ts';
import { CloseIcon } from './Icons.tsx';

type Step = 'loading' | 'contact' | 'code' | 'handle';

/**
 * One flow for "Sign in" and "Make it yours": email or phone → code → (optional) claim a link.
 * Existing account: signs in, and anything this device streamed as a guest moves into it.
 * New contact: creates the account from the guest session. Nobody needs this to go live.
 * The /signin page shows it full size; the Go Live page in a sheet, so the broadcast keeps going.
 */
export function SignInFlow({ intent, intro, initialMode = 'email', initialHandle = '', skipHandle = false, compact = false, onDone }: {
  intent: 'signin' | 'claim';
  /** The line under the heading before the code is sent. */
  intro: string;
  initialMode?: 'email' | 'phone';
  /** A link picked on the home page (/claim?handle=…), filled in for the last step. */
  initialHandle?: string;
  /** Done as soon as they're signed in, without offering a link (e.g. back to a page to turn on alerts). */
  skipHandle?: boolean;
  /** Smaller heading, for the sheet. */
  compact?: boolean;
  /** Signed in, and the link claimed or skipped. */
  onDone: () => void;
}) {
  const [step, setStep] = useState<Step>('loading');
  const [mode, setMode] = useState<'email' | 'phone'>(initialMode);
  const [contact, setContact] = useState('');
  const [country, setCountry] = useState(guessCountry);
  /** Shown once the person leaves the field or submits, and updated live after that. */
  const [showCheck, setShowCheck] = useState(false);
  /** The server's verdict on this exact entry (e.g. a disposable inbox), shown under the field. */
  const [rejected, setRejected] = useState<{ value: string; message: string } | null>(null);
  const [code, setCode] = useState('');
  const [handle, setHandle] = useState(() => cleanHandleInput(initialHandle));
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Localhost only: no SMS or email goes out there, so the API hands the code to the screen. */
  const [devCode, setDevCode] = useState<string | null>(null);

  useEffect(() => {
    // A guest session first, so a later sign-in can carry this device's streams with it.
    void ensureSession()
      .then(() => api.me())
      .then((me) => {
        if (!me.user?.isVerified) return setStep('contact');
        if (skipHandle || me.channel?.handle) return onDone();
        setStep('handle');
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  const status = useHandleCheck(handle, step === 'handle');

  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const check = mode === 'email' ? checkEmail(contact) : checkPhone(contact, country);
  const target = check.ok ? ('email' in check ? check.email : check.e164) : '';
  const shownTarget = mode === 'phone' && check.ok ? `+${country.dial} ${formatNational(nationalDigits(contact, country), country)}` : target;
  const fieldError = showCheck && !check.ok ? check.message : rejected?.value === contact ? rejected.message : null;

  const afterVerify = async () => {
    const me = await refreshMe();
    if (skipHandle || me?.channel?.handle) onDone();
    else setStep('handle');
  };

  const heading =
    step === 'code' ? (mode === 'email' ? ['Check your', 'inbox.'] : ['Check your', 'messages.'])
    : step === 'handle' ? ['Claim your', 'link.']
    : intent === 'claim' ? ['Make it', 'yours.'] : ['Sign', 'in.'];
  const input = 'h-14 w-full rounded-2xl border-2 border-line bg-white px-4 text-lg text-navy outline-none focus:border-navy';
  const fieldLabel = 'block text-xs font-extrabold tracking-[0.08em] text-muted uppercase';

  return (
    <>
      {!compact && <span className="speed-stripes" aria-hidden />}
      <h1 className={`speed-text leading-[0.95] ${compact ? 'pr-12 text-[34px]' : 'mt-4 text-[44px]'}`}>
        {heading[0]} <span className="text-race">{heading[1]}</span>
      </h1>
      {step !== 'code' && step !== 'handle' && <p className={`mt-3 leading-relaxed text-muted ${compact ? 'text-[15px]' : 'text-[17px]'}`}>{intro}</p>}

      {step === 'contact' && (
        <form noValidate className={`${compact ? 'mt-5' : 'mt-7'} space-y-4`} onSubmit={(e) => { e.preventDefault(); setShowCheck(true); if (!check.ok) return; void act(async () => {
          try {
            if (mode === 'email') await emailCode.send(target);
            else await phoneCode.send(target);
          } catch (err) {
            // Problems with the entry itself belong under the field, not in a banner.
            const message = (err as Error).message;
            if (/valid|without the \+/i.test(message)) return setRejected({ value: contact, message });
            throw err;
          }
          setStep('code');
          if (import.meta.env.DEV) {
            const r = await fetch(`/api/v1/dev/otp?to=${encodeURIComponent(target)}`, { credentials: 'include' }).catch(() => null);
            setDevCode(r?.ok ? ((await r.json()) as { code: string | null }).code : null);
          }
        }); }}>
          <div className="grid grid-cols-2 rounded-2xl bg-mist p-1 text-[15px]" role="tablist">
            {(['email', 'phone'] as const).map((m) => (
              <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => { setMode(m); setContact(''); setShowCheck(false); }}
                className={`h-11 rounded-xl font-extrabold ${mode === m ? 'bg-white text-navy shadow-sm' : 'text-muted'}`}>
                {m === 'email' ? 'Email' : 'Phone'}
              </button>
            ))}
          </div>
          <label className={fieldLabel} htmlFor="contact">{mode === 'email' ? 'Your email' : 'Your mobile number'}</label>
          {mode === 'email' ? (
            <input id="contact" type="email" autoComplete="email" inputMode="email" autoCapitalize="off" spellCheck={false} maxLength={254}
              placeholder="you@example.com" aria-invalid={Boolean(fieldError)} aria-describedby="contact-error"
              value={contact} onChange={(e) => setContact(e.target.value)} onBlur={() => contact && setShowCheck(true)}
              className={`${input} ${fieldError ? '!border-race' : ''}`} />
          ) : (
            <div className={`flex h-14 overflow-hidden rounded-2xl border-2 bg-white ${fieldError ? 'border-race' : 'border-line focus-within:border-navy'}`}>
              {/* The closed picker shows just 🇺🇸 +1; the native list (best on phones) shows full names. */}
              <div className="relative flex shrink-0 items-center gap-1.5 border-r-2 border-line bg-mist pr-2.5 pl-3 text-base font-bold text-navy focus-within:bg-line">
                <span aria-hidden className="text-xl leading-none">{flag(country.iso)}</span>
                <span aria-hidden>+{country.dial}</span>
                <svg aria-hidden width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                <label className="sr-only" htmlFor="country">Country</label>
                <select id="country" value={country.iso} autoComplete="tel-country-code"
                  onChange={(e) => setCountry(COUNTRIES.find((c) => c.iso === e.target.value)!)}
                  className="absolute inset-0 cursor-pointer opacity-0">
                  {COUNTRIES.map((c) => <option key={c.iso} value={c.iso}>{flag(c.iso)} {c.name} (+{c.dial})</option>)}
                </select>
              </div>
              <input id="contact" type="tel" inputMode="tel" autoComplete="tel-national" maxLength={22}
                placeholder={country.dial === '1' ? '555 123 4567' : 'Mobile number'} aria-invalid={Boolean(fieldError)} aria-describedby="contact-error"
                value={contact}
                // Digits, spaces and common separators only; letters and anything else never reach the field.
                onChange={(e) => {
                  const next = e.target.value.replace(/[^0-9+\s().-]/g, '');
                  // Stop at the longest valid length for this country (a pasted +code or trunk 0 is allowed for).
                  if (nationalDigits(next, country).length <= Math.max(...country.lengths)) setContact(next);
                }}
                onBlur={() => contact && setShowCheck(true)}
                className="h-full min-w-0 flex-1 px-4 text-lg text-navy outline-none" />
            </div>
          )}
          <p id="contact-error" role="alert" className="min-h-5 text-sm font-bold text-race-600">{fieldError}</p>
          <button disabled={busy} className="btn-race h-15 w-full text-xl">Send my code</button>
        </form>
      )}

      {step === 'code' && (
        <form className="mt-5 space-y-4" onSubmit={(e) => { e.preventDefault(); void act(async () => {
          if (mode === 'email') await emailCode.verify(target, code.trim());
          else await phoneCode.verify(target, code.trim());
          await afterVerify();
        }); }}>
          <label className="block text-[17px] leading-relaxed text-muted" htmlFor="code">
            Enter the code we sent to <b className="text-navy">{shownTarget}</b>. No password, ever.
          </label>
          <input id="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{4,8}" maxLength={8} required value={code} onChange={(e) => setCode(e.target.value)}
            className="h-16 w-full rounded-2xl border-2 border-navy bg-white text-center font-mono text-3xl tracking-[0.35em] text-navy outline-none focus:ring-4 focus:ring-race/20" />
          {import.meta.env.DEV && devCode && (
            <p className="flex items-center justify-between gap-3 rounded-2xl border-2 border-dashed border-line px-4 py-3 text-sm">
              <span>
                <b>Localhost:</b> no {mode === 'email' ? 'email' : 'text'} is sent. Your code is <b className="font-mono">{devCode}</b>
              </span>
              <button type="button" className="shrink-0 font-extrabold underline underline-offset-2" onClick={() => setCode(devCode)}>Fill it in</button>
            </p>
          )}
          <button disabled={busy} className="btn-race h-15 w-full text-xl">Verify</button>
          <button type="button" className="h-11 w-full font-bold underline underline-offset-2" onClick={() => { setCode(''); setShowCheck(false); setStep('contact'); }}>Use a different {mode === 'email' ? 'email' : 'number'}</button>
        </form>
      )}

      {step === 'handle' && (
        <form className="mt-6 space-y-4" onSubmit={(e) => { e.preventDefault(); void act(async () => {
          await api.claimHandle(handle);
          if (name.trim()) await api.setDisplayName(name.trim());
          await refreshMe();
          onDone();
        }); }}>
          <p className="rounded-2xl bg-ok/10 px-4 py-3 text-[15px] font-bold text-ok">✓ You’re signed in. Your streams are saved to your account.</p>
          <label className={fieldLabel} htmlFor="name">Display name</label>
          <input id="name" value={name} maxLength={50} onChange={(e) => setName(e.target.value)} className={input} />
          <label className={fieldLabel} htmlFor="handle">Your link</label>
          <div className={`flex h-14 items-center rounded-2xl border-2 bg-white px-4 text-lg ${status.kind === 'ok' ? 'border-ok' : status.kind === 'bad' ? 'border-race' : 'border-line focus-within:border-navy'}`}>
            <span className="text-muted">zip2live.com/</span>
            <input id="handle" required value={handle} maxLength={24} autoCapitalize="off" autoCorrect="off" spellCheck={false} aria-describedby="handle-status" aria-invalid={status.kind === 'bad'}
              onChange={(e) => setHandle(cleanHandleInput(e.target.value))}
              className="h-full min-w-0 flex-1 font-extrabold outline-none" />
            <span aria-hidden className="ml-2 inline-flex w-6 shrink-0 justify-center">
              {status.kind === 'checking' && <span className="h-5 w-5 animate-spin rounded-full border-2 border-line border-t-navy" />}
              {status.kind === 'ok' && <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-ok)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>}
              {status.kind === 'bad' && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-race)" strokeWidth="3" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>}
            </span>
          </div>
          <p id="handle-status" role="status" aria-live="polite" className={`min-h-5 text-sm font-bold ${status.kind === 'ok' ? 'text-ok' : status.kind === 'bad' ? 'text-race-600' : 'text-muted'}`}>
            {status.kind === 'checking' ? 'Checking…' : status.kind === 'ok' ? `zip2live.com/${status.handle} is yours to claim` : status.kind === 'bad' ? status.message : '3–24 letters, numbers, - or _'}
          </p>
          <button disabled={busy || status.kind === 'bad' || status.kind === 'checking'} className="btn-race h-15 w-full text-xl">Claim my link</button>
          <button type="button" className="h-11 w-full font-bold underline underline-offset-2" onClick={onDone}>Skip for now</button>
        </form>
      )}

      {step === 'loading' && !error && <p className="mt-8 font-semibold">Loading…</p>}
      {error && <p role="alert" className="mt-5 rounded-xl bg-race/10 px-4 py-3 font-semibold text-race-600">{error}</p>}
      {step === 'code' && (
        <p className={`${compact ? 'mt-5' : 'mt-8'} rounded-2xl bg-mist px-4 py-3.5 text-sm leading-relaxed`}>New here? This creates your account and keeps everything you streamed on this device.</p>
      )}
    </>
  );
}

/**
 * Sign in without leaving the page: the Go Live page, where leaving would stop the broadcast.
 * The stream moves into the account the moment the code is verified, with the same link.
 */
export function SignInSheet({ intro, onClose, onDone }: { intro: string; onClose: () => void; onDone: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center" role="dialog" aria-modal="true" aria-label="Sign in" onClick={onClose}>
      <div
        className="relative max-h-[92dvh] w-full max-w-md overflow-y-auto rounded-t-[28px] bg-white p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-navy sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" aria-label="Close" onClick={onClose} className="absolute top-4 right-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-mist hover:bg-line">
          <CloseIcon />
        </button>
        <SignInFlow compact intent="signin" intro={intro} onDone={onDone} />
      </div>
    </div>
  );
}
